import { CoreLayoutView, TCoreLayoutStyleProps } from '@core/widgets';
import clsx from 'clsx';
import React from 'react';
import { TVerticalLayoutOption } from '../../types/options';
import { getBackgroundHref, resolveCssColor } from '../Image/imageUtils';
import styles from './css/verticalLayout.module.css';

const toLine = (value?: string): TCoreLayoutStyleProps['lineY'] => (value ? (value.replace(/-/g, '_') as TCoreLayoutStyleProps['lineY']) : undefined);

export type TVerticalLayoutViewProps = {
    /** Value for the `data-test` attribute (widget uid in server-driven scenarios). */
    uid?: string;
    /** Pre-resolved visibility state (derived from useBehavior one level up). */
    isHide?: boolean;
    /** Server-driven theme style name forwarded to CoreLayoutView for theme resolution. */
    styleName?: string;
    children?: React.ReactNode;
    /** Design version of the owning widget: a semantic `backgroundColor` resolves to that version's token. */
    version: number | undefined;
} & Pick<TVerticalLayoutOption, 'margin' | 'alignment' | 'arrangement' | 'backgroundColor' | 'backgroundImage'>;

/**
 * Composable vertical layout building block for use inside complex widgets.
 * Owns only rendering logic — the wrapper element and the mapping of
 * alignment/arrangement options into CoreLayout style props.
 *
 * Accepts pre-resolved behavior props (isHide) and plain display values from
 * the parent. It NEVER receives a `widget` object — every value is an explicit
 * prop, resolved one level up (see VerticalLayoutWidget).
 */
const VerticalLayoutView = ({
    uid,
    isHide,
    alignment,
    arrangement,
    styleName,
    margin,
    backgroundColor,
    backgroundImage,
    children,
    version,
}: TVerticalLayoutViewProps) => {
    const styleProps: TCoreLayoutStyleProps = {
        lineY: toLine(arrangement),
        lineX: alignment === 'center' ? 'center' : alignment === 'bottom' ? 'bottom' : 'stretch',
        backgroundColor: resolveCssColor(backgroundColor, version),
        backgroundImage: getBackgroundHref(backgroundImage),
        margin,
    };

    return (
        <div data-test={uid} className={clsx('vertical-layout', styles.verticalLayout)}>
            <CoreLayoutView uid={'verticalLayoutInner_' + uid} styleProps={styleProps} isHide={isHide} styleName={styleName}>
                {children}
            </CoreLayoutView>
        </div>
    );
};

export { VerticalLayoutView };
